import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { IRMA_FRAMEWORK, IMAGES } from "../data/config";
import { Reveal } from "../components/common/Reveal";

const ROLES = ["Dentist", "Entrepreneur", "Consultant", "Speaker", "Author"];

const CLINICAL_STEPS = ["We observe.", "We ask.", "We diagnose.", "Then we intervene."];

export default function AboutPage() {
  return (
    <main data-testid="about-page">
      <section className="bg-cream py-24 lg:py-32" data-testid="about-hero">
        <div className="container-site">
          <Reveal>
            <p className="eyebrow">About</p>
            <h1 className="mt-4 max-w-3xl font-serif text-4xl leading-[1.08] tracking-tight text-navy sm:text-5xl lg:text-6xl">
              REAL EXPERIENCE. <span className="italic text-emerald">HUMAN GROWTH.</span>
            </h1>
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-20 lg:py-28" data-testid="about-profile">
        <div className="container-site grid items-start gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-4">
            <div className="relative">
              <div className="absolute -left-4 -top-4 h-full w-full border border-gold/50" aria-hidden="true" />
              <img
                src={IMAGES.portrait}
                alt="Dr. Irma Shida Abu Samah"
                loading="lazy"
                className="relative aspect-[4/5] w-full object-cover object-top"
                data-testid="about-portrait"
              />
            </div>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-8 lg:pl-8">
            <h2 className="font-serif text-3xl tracking-tight text-navy sm:text-4xl">
              Dr. Irma Shida <span className="italic text-emerald">Abu Samah</span>
            </h2>
            <p className="mt-2 text-xs font-semibold uppercase tracking-[0.25em] text-gold">
              DDS, UKM
            </p>
            <ul className="mt-6 flex flex-wrap gap-2" aria-label="Roles">
              {ROLES.map((r) => (
                <li
                  key={r}
                  className="border border-navy/15 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.15em] text-ink/70"
                >
                  {r}
                </li>
              ))}
            </ul>
            <div className="mt-8 max-w-2xl space-y-5 text-base leading-relaxed text-ink/75">
              <p>
                For approximately two decades, Dr. Irma has been a dental
                professional and practice owner — years spent understanding how
                people behave when they are uncertain, hopeful, afraid or ready
                to change.
              </p>
              <p>
                Alongside clinical practice, she built businesses and led teams —
                living the realities of entrepreneurship firsthand: the growth
                seasons and the setbacks, the decisions that worked and the ones
                that had to be rebuilt.
              </p>
              <p>
                Today, through Irma Ariandi Global, she brings that lived
                experience into consulting, training, speaking and writing — a
                practical, human-centred approach shaped by the discipline of
                clinical thinking.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-sand py-20 lg:py-28" data-testid="about-clinical-thinking">
        <div className="container-site grid gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <p className="eyebrow">Why clinical thinking?</p>
            <h2 className="mt-4 font-serif text-3xl tracking-tight text-navy sm:text-4xl">
              A clinician does not treat <span className="italic text-emerald">only the symptom.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-7">
            <ol className="space-y-0">
              {CLINICAL_STEPS.map((s, i) => (
                <li
                  key={s}
                  className="flex items-center gap-6 border-b border-navy/10 py-5 last:border-b-0"
                  data-testid={`clinical-step-${i + 1}`}
                >
                  <span className="font-serif text-2xl text-gold" aria-hidden="true">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <p className="font-serif text-xl italic text-navy">{s}</p>
                </li>
              ))}
            </ol>
            <p className="mt-8 max-w-xl text-base leading-relaxed text-ink/75">
              That same discipline informs Dr. Irma&apos;s approach to people,
              business and organisations — understand the diagnosis before
              designing the intervention.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-navy py-20 text-cream lg:py-28" data-testid="about-framework">
        <div className="container-site">
          <Reveal>
            <p className="eyebrow">Framework IRMA</p>
            <div className="mt-12 grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
              {IRMA_FRAMEWORK.map((f, i) => (
                <div key={f.step} data-testid={`about-irma-step-${i + 1}`}>
                  <div className="h-0.5 w-10 bg-gold" aria-hidden="true" />
                  <p className="mt-6 font-serif text-2xl tracking-tight">{f.step}</p>
                  <p className="mt-3 text-sm leading-relaxed text-cream/70">{f.description}</p>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-20 border-t border-cream/10 pt-16">
              <p className="font-serif text-4xl italic leading-tight tracking-tight text-gold sm:text-5xl" data-testid="about-closing">
                Let&apos;s Inspire Possibilities.
              </p>
              <Link to="/contact" className="btn-gold mt-10" data-testid="about-cta">
                Work With Us <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
